import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listRecruitingNotes } from '../../../../services/recruiting-notes';
import type { RecruitingNoteResponse } from '../../../../types/api';
import { ErrorBoundary } from '../../../../components/ErrorBoundary';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';
import { LatestNoteRow } from './LatestNoteRow';
import { tourAnchor } from '../../../tours/tourAnchors';
import { useTour } from '../../../tours/useTour';
import { RecruitingNoteViewerModal } from '../players/documents/RecruitingNoteViewerModal';
import './LatestNotesSection.css';

const PAGE_SIZE = 20;

/**
 * Latest Notes feed — a read-only chronological list of recruiting notes
 * assigned to prospects, scoped to the current organization. Clicking a row
 * opens that note directly in the viewer modal.
 *
 * Refreshes on page load and when the browser tab regains visibility (tab
 * focus equivalent) so notes created on mobile appear without a manual reload.
 */
export const LatestNotesSection = () => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState<RecruitingNoteResponse[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewingNote, setViewingNote] = useState<RecruitingNoteResponse | null>(null);
  // Tracks the most recent fetch so racing requests (e.g. a focus refresh
  // landing between a pagination load and its response) don't overwrite
  // newer state with stale results.
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(
    async (pageNumber: number, append: boolean) => {
      const requestId = ++requestIdRef.current;
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }
        setError(null);

        const response = await listRecruitingNotes({
          page: pageNumber,
          pageSize: PAGE_SIZE,
          assignedOnly: true,
        });

        // Ignore responses from superseded requests
        if (requestId !== requestIdRef.current) return;

        setNotes(prev => (append ? [...prev, ...response.items] : response.items));
        setHasMore(response.hasMore);
        setPage(response.pageNumber);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setError(getSafeErrorMessage(err, 'Failed to load latest notes'));
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [],
  );

  // Initial load
  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  // Refresh when the tab regains visibility (equivalent to mobile's
  // useFocusEffect). Covers the common case where a coach creates a note on
  // mobile and then switches to the desktop tab.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchPage(1, false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchPage]);

  const handleRowClick = useCallback((note: RecruitingNoteResponse) => {
    setViewingNote(note);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!hasMore || loadingMore) return;
    fetchPage(page + 1, true);
  }, [hasMore, loadingMore, page, fetchPage]);

  const handleCreateNote = useCallback(() => {
    navigate('/recruiting-notes/new');
  }, [navigate]);

  useTour('notes', { ready: !loading, hasData: notes.length > 0 });

  return (
    <ErrorBoundary
      fallbackTitle="Latest Notes Error"
      fallbackMessage="We encountered an error loading latest notes. This may be temporary."
    >
      <div className="latest-notes-section">
        <div className="latest-notes-section__header">
          <h2 className="latest-notes-section__title" {...tourAnchor('notes.title')}>
            Latest Notes
          </h2>
          <div className="latest-notes-section__header-actions">
            <button
              type="button"
              className="latest-notes-section__create-btn"
              {...tourAnchor('notes.new')}
              onClick={handleCreateNote}
            >
              + New Note
            </button>
          </div>
        </div>

        {error && <div className="latest-notes-section__error">{error}</div>}

        {loading && (
          <div className="latest-notes-section__loading">
            <div className="latest-notes-section__spinner" />
            <p>Loading latest notes...</p>
          </div>
        )}

        {!loading && !error && notes.length === 0 && (
          <div className="latest-notes-section__empty" {...tourAnchor('notes.list')}>
            <p className="latest-notes-section__empty-title">No assigned notes yet</p>
            <p className="latest-notes-section__empty-subtitle">
              Notes attached to a prospect will appear here once your team starts adding them
              from the mobile app.
            </p>
          </div>
        )}

        {!loading && notes.length > 0 && (
          <ul className="latest-notes-section__list" {...tourAnchor('notes.list')}>
            {notes.map((note, index) => (
              <li key={note.id}>
                <LatestNoteRow
                  note={note}
                  onClick={() => handleRowClick(note)}
                  anchor={index === 0 ? 'notes.row.open' : undefined}
                />
              </li>
            ))}
          </ul>
        )}

        {hasMore && !loading && (
          <div className="latest-notes-section__load-more">
            <button
              className="latest-notes-section__load-more-btn"
              {...tourAnchor('notes.more')}
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : 'Load more'}
            </button>
          </div>
        )}
      </div>

      <RecruitingNoteViewerModal
        note={viewingNote}
        onClose={() => setViewingNote(null)}
      />
    </ErrorBoundary>
  );
};
